/**
 * A finding's events as rows of its one timeline: a small icon on the rail,
 * a muted one-liner ("Ann Kerr resolved it · 3d ago") and the detail under it.
 * Comments and Inspector's replies stay cards; these sit between them.
 *
 * @module components/regulation/finding/findingActivityTimeline
 */
import {
  ArchiveIcon,
  ChevronDownIcon,
  CircleCheckIcon,
  CircleDotIcon,
  CircleSlashIcon,
  FileTextIcon,
  HistoryIcon,
  RotateCcwIcon,
  ScanSearchIcon,
  ShieldCheckIcon,
  ShieldOffIcon,
  Undo2Icon,
  UserPlusIcon,
  WandSparklesIcon,
} from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { Button } from "../../ui/button";
import { isClamped } from "../ClampedText";
import type { ReviewTimelineEntry } from "../ReviewConversation";
import type { FindingActivityEvent, FindingActivityTone } from "./findingActivity";
import { relativeAge } from "./FindingsQueue.logic";

const ICON: Readonly<Record<FindingActivityTone, ReactNode>> = {
  raised: <ScanSearchIcon className="size-3.5" />,
  resolved: <CircleCheckIcon className="size-3.5 text-emerald-600 dark:text-emerald-400" />,
  dismissed: <CircleSlashIcon className="size-3.5" />,
  reopened: <RotateCcwIcon className="size-3.5" />,
  planned: <UserPlusIcon className="size-3.5" />,
  revised: <HistoryIcon className="size-3.5" />,
  withdrawn: <ArchiveIcon className="size-3.5" />,
  undone: <Undo2Icon className="size-3.5" />,
  proposed: <WandSparklesIcon className="size-3.5" />,
  progress: <CircleDotIcon className="size-3.5" />,
  checked: <ShieldCheckIcon className="size-3.5 text-emerald-600 dark:text-emerald-400" />,
  unchecked: <ShieldOffIcon className="size-3.5" />,
  document: <FileTextIcon className="size-3.5" />,
  other: <HistoryIcon className="size-3.5" />,
};

/**
 * An event's detail (a proposal's summary can run to a paragraph; the fix
 * itself is above): two lines, and a standard micro button under them only
 * when something is actually hidden — no fade or overlaid link.
 */
function ActivityDetail({ children }: { readonly children: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const id = useId();
  const measure = useCallback(() => {
    if (ref.current) setOverflows(isClamped(ref.current));
  }, []);
  useEffect(() => {
    if (expanded) return;
    measure();
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [expanded, measure, children]);
  return (
    <div className="mt-0.5 min-w-0">
      <p id={id} ref={ref} className={cn("whitespace-pre-line break-words text-muted-foreground/90", !expanded && "line-clamp-2")}>
        {children}
      </p>
      {overflows || expanded ? (
        <Button
          size="micro"
          variant="ghost-muted"
          className="-ms-1.5 mt-0.5"
          aria-expanded={expanded}
          aria-controls={id}
          onClick={() => setExpanded((value) => !value)}
          data-activity-detail-toggle=""
        >
          {expanded ? "Show less" : "Show more"}
          <ChevronDownIcon aria-hidden className={cn("transition-transform duration-150", expanded && "rotate-180")} />
        </Button>
      ) : null}
    </div>
  );
}

export function FindingActivityRow({ event }: { readonly event: FindingActivityEvent }) {
  const at = Date.parse(event.createdAt);
  return (
    <div className="text-xs leading-5 text-muted-foreground" data-activity-event={event.tone}>
      <p>
        <span className="font-medium text-foreground/80">{event.actor}</span> {event.text}
        {Number.isNaN(at) ? null : (
          <time dateTime={event.createdAt} title={new Date(at).toLocaleString("en-GB")} className="text-muted-foreground/80">
            {" "}
            · {relativeAge(at) === "now" ? "just now" : `${relativeAge(at)} ago`}
          </time>
        )}
      </p>
      {event.detail ? <ActivityDetail>{event.detail}</ActivityDetail> : null}
    </div>
  );
}

/** The events as timeline entries, ready to merge with the conversation's. */
export function findingActivityTimeline(events: ReadonlyArray<FindingActivityEvent>): ReviewTimelineEntry[] {
  return events.map((event) => ({
    id: `activity:${event.id}`,
    createdAt: event.createdAt,
    order: -1,
    compact: true,
    indicator: <span className="text-muted-foreground">{ICON[event.tone]}</span>,
    content: <FindingActivityRow event={event} />,
  }));
}
